import { useState, useEffect, useRef } from "react";
import { orgApi } from "../utils/apiV2";
import { Button } from "./ui/button";
import FlipToggle from "./FlipToggle";
import ServiceToggle from "./ServiceToggle";
import { 
  AlertTriangle, FileText, CreditCard, Plus, ChevronDown, Link2,
  Share2, Edit, Trash2, Package, ArrowRight, AlertCircle, Loader2, Check,
  LayoutGrid, List, Info
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import CreateServiceWizard from "./CreateServiceWizard";
import ServiceSharingModal from "./ServiceSharingModal";
import TabPageLayout from './TabPageLayout';
import EmptyState from './EmptyState';
import CardListViewToggle from './CardListViewToggle';
import { PrimaryButton, SecondaryButton, DangerButton, LayoutActionButton } from './Buttons';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import { Badge } from "./ui/badge";

const ServicesTabImproved = ({ onNavigate }) => {
  const [services, setServices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const addDropdownRef = useRef(null);
  const [showDuplicatePicker, setShowDuplicatePicker] = useState(false);
  const [duplicateSearch, setDuplicateSearch] = useState('');
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [viewMode, setViewMode] = useState("card");
  
  // Delete modal state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, service: null, hasBookings: false, checkingBookings: false });
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Service sharing modal state
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [sharingService, setSharingService] = useState(null);
  
  // Business-critical gate checks
  const [hasWaiverText, setHasWaiverText] = useState(false);
  const [hasStripeConnect, setHasStripeConnect] = useState(false);
  const [gateCheckLoading, setGateCheckLoading] = useState(true);

  useEffect(() => {
    loadServices();
    loadAssets();
    checkGateRequirements();
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(event.target)) {
        setShowAddDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkGateRequirements = async () => {
    try {
      setGateCheckLoading(true);
      const orgResponse = await orgApi.getOrganization();
      const org = orgResponse.data;
      
      const waiverText = org.customWaiverText || '';
      setHasWaiverText(waiverText.trim().length >= 100);
      setHasStripeConnect(!!(org.stripeConnectAccountId && org.stripeConnectChargesEnabled));
    } catch (error) {
      console.error("Error checking gate requirements:", error);
    } finally {
      setGateCheckLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await orgApi.getServices({ activeOnly: false });
      
      // Map v2 response to component format
      const servicesData = response.data?.services || response.data || [];
      const mappedServices = servicesData.map(s => ({
        ...s,
        // Ensure legacy field names are available
        // 'active' is the Live/Off toggle, 'isActive' is for soft-delete/deactivation
        active: s.active ?? s.isActive,
        price: s.basePrice,
        duration: s.durationMinutes,
        sortOrder: s.displayOrder ?? 999
      }));
      
      // Sort by displayOrder to maintain correct order
      mappedServices.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
      
      setServices(mappedServices);
    } catch (error) {
      console.error("Error loading services:", error);
      setError("Failed to load services");
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    try {
      setAssetsLoading(true);
      const response = await orgApi.getAssets();
      const assetsData = response.data?.assets || response.data || [];
      setAssets(assetsData);
    } catch (error) {
      console.error("Error loading assets:", error);
    } finally {
      setAssetsLoading(false);
    }
  };

  const canReceivePayments = hasWaiverText && hasStripeConnect;

  const openServiceModal = async (service = null) => {
    // Allow creating/editing services even without Stripe/waiver
    // (they can demo the software, just can't receive payments)
    
    // Refresh assets so newly added ones appear in the Link Assets step
    loadAssets();
    
    // If editing, fetch the full service data with ALL fields
    if (service) {
      try {
        const response = await orgApi.getService(service.id);
        setEditingService(response.data);
      } catch (error) {
        console.error('Error fetching service details:', error);
        toast.error('Failed to load service details');
        // Fall back to partial data
        setEditingService(service);
      }
    } else {
      setEditingService(null);
    }
    
    setShowServiceModal(true);
  };

  const duplicateService = async (service) => {
    setShowDuplicatePicker(false);
    setShowAddDropdown(false);
    loadAssets(); // refresh so the Link Assets step is current
    const loadingToast = toast.loading(`Duplicating "${service.name}"...`);
    try {
      // Fetch full service data (all fields)
      const response = await orgApi.getService(service.id);
      const full = response.data;
      // Strip identity fields so wizard treats it as a new service
      const { id, organizationId, createdAt, updatedAt, displayOrder, sortOrder, ...rest } = full;
      // Mark as a duplicate so wizard starts fresh
      setEditingService({
        ...rest,
        name: `Copy of ${full.name}`,
        active: false,      // start off / not live
        isActive: true,
      });
      setShowServiceModal(true);
      toast.dismiss(loadingToast);
      toast.success('Service duplicated — review and save');
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error('Failed to load service for duplication');
    }
  };

  const handleDeleteService = async () => {
    if (!deleteConfirm.service) return;
    
    // If service is still Live, warn them to turn it off first
    if (deleteConfirm.service.active) {
      toast.error("Please turn the service 'Off' before deleting", { duration: 4000 });
      return;
    }
    
    setIsDeleting(true);
    const loadingToast = toast.loading(`Deleting ${deleteConfirm.service.name}...`);
    
    try {
      const response = await orgApi.deleteService(deleteConfirm.service.id);
      toast.dismiss(loadingToast);
      toast.success("Service deleted");
      setServices(prev => prev.filter(s => s.id !== deleteConfirm.service.id));
      setDeleteConfirm({ open: false, service: null, hasBookings: false, checkingBookings: false });
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error("Error deleting service:", error);
      const errorDetail = error.response?.data?.detail;
      
      // Check if error is about future bookings
      if (errorDetail?.includes('booking') || errorDetail?.includes('trips')) {
        toast.error("This service has upcoming bookings. Keep it turned off until all trips are completed, then you can delete it.", { duration: 6000 });
      } else {
        toast.error(errorDetail || "Failed to delete service", { duration: 6000 });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Open delete confirmation - check if service has future bookings
  const openDeleteConfirm = async (service) => {
    // If service is Live, warn them first
    if (service.active) {
      toast.info("Turn the service 'Off' first, then you can delete it", { duration: 4000 });
      return;
    }
    
    setDeleteConfirm({ open: true, service, hasBookings: false, checkingBookings: true });
    
    // Check for future bookings
    try {
      const response = await orgApi.getBookings({ 
        serviceId: service.id, 
        status: 'upcoming',
        limit: 1 
      });
      const hasUpcoming = response.data?.bookings?.length > 0 || response.data?.length > 0;
      setDeleteConfirm(prev => ({ ...prev, hasBookings: hasUpcoming, checkingBookings: false }));
    } catch (error) {
      console.error("Error checking bookings:", error);
      setDeleteConfirm(prev => ({ ...prev, checkingBookings: false }));
    }
  };

  // Track which service is being toggled
  const [togglingServiceId, setTogglingServiceId] = useState(null);

  const handleToggleServiceActive = async (service, newActiveState) => {
    setTogglingServiceId(service.id);
    const loadingToast = toast.loading(newActiveState ? `Turning ${service.name} Live...` : `Turning ${service.name} Off...`);
    
    try {
      // Only update 'active' field - this controls visibility in flows/widget
      // NOT 'isActive' which is the deactivation status
      await orgApi.updateService(service.id, {
        active: newActiveState
      });
      
      toast.dismiss(loadingToast);
      setServices(prev => prev.map(s => 
        s.id === service.id ? { ...s, active: newActiveState } : s
      ));
      
      toast.success(newActiveState ? `${service.name} is now Live` : `${service.name} is now Off`);
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error("Error toggling service:", error);
      toast.error("Failed to update service status");
      loadServices();
    } finally {
      setTogglingServiceId(null);
    }
  };

  // Error state
  if (error) {
    return (
      <TabPageLayout
        title="Services"
        subtitle="Manage bookable services"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load services</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <PrimaryButton onClick={() => loadServices()}>
            Try Again
          </PrimaryButton>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Services"
      subtitle="Manage bookable services"
      loading={loading}
      actions={
        <div className="flex items-center gap-2">
          {/* + Add dropdown — New Service or Duplicate */}
          <div className="relative" ref={addDropdownRef}>
            <LayoutActionButton
              onClick={() => setShowAddDropdown(prev => !prev)}
              icon={Plus}
              disabled={assetsLoading || loading}
              data-testid="add-service-btn"
            >
              Add
              <ChevronDown className={`w-3 h-3 transition-transform ${showAddDropdown ? 'rotate-180' : ''}`} />
            </LayoutActionButton>

            {showAddDropdown && (
              <div className="absolute right-0 top-full mt-1.5 w-52 bg-white rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.14)] z-50 overflow-hidden">
                <button
                  data-testid="new-service-option"
                  onClick={() => { setShowAddDropdown(false); openServiceModal(); }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Plus className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">New Service</div>
                    <div className="text-xs text-gray-500">Start from scratch</div>
                  </div>
                </button>
                <div className="border-t border-gray-100" />
                <button
                  data-testid="duplicate-service-option"
                  onClick={() => { setShowAddDropdown(false); setShowDuplicatePicker(true); setDuplicateSearch(''); }}
                  disabled={services.length === 0}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="w-7 h-7 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Duplicate Service</div>
                    <div className="text-xs text-gray-500">Copy an existing one</div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Setup Reminder Banner - shown when Stripe/waiver not complete */}
        {!gateCheckLoading && !canReceivePayments && (
          <div className="card-soft p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl font-medium text-gray-900 tracking-tight">Complete setup to receive payments</h3>
                <p className="text-sm text-gray-500 mt-1 font-light">
                  You can create and preview services now — finish these steps when you&apos;re ready to accept real bookings.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {!hasWaiverText && (
                <button
                  onClick={() => onNavigate && onNavigate('waivers')}
                  className="group flex items-center gap-3 p-4 rounded-xl bg-white shadow-[0_2px_10px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Set up your waiver</p>
                    <p className="text-xs text-gray-500 mt-0.5 font-light">Add waiver text (100+ characters) to protect your business</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </button>
              )}
              {!hasStripeConnect && (
                <button
                  onClick={() => onNavigate && onNavigate('payments')}
                  className="group flex items-center gap-3 p-4 rounded-xl bg-white shadow-[0_2px_10px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">Connect Stripe</p>
                    <p className="text-xs text-gray-500 mt-0.5 font-light">Set up payment processing to accept bookings</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Services Content */}
        {services.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No Services Yet"
            description="Create your first service to start accepting bookings"
            actionLabel="Create Service"
            onAction={() => openServiceModal()}
          />
        ) : viewMode === "card" ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service, index) => {
              const isLive = service.active === true;
              
              return (
                <div 
                  key={service.id} 
                  className="card-soft overflow-hidden transition-all relative group hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)]"
                >
                  {/* Image */}
                  <div className="relative h-40 sm:h-48 bg-gray-100">
                    {service.images && service.images.length > 0 ? (
                      <img 
                        src={service.images[service.mainImageIndex || 0]} 
                        alt={service.name} 
                        className={`w-full h-full object-cover ${!isLive ? 'opacity-70' : ''}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-gray-300" />
                      </div>
                    )}
                    
                  </div>
                  
                  {/* Content */}
                  <div className="pt-4 px-4">
                    {/* Title + toggle justify-between */}
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <h3 className="font-medium text-base text-gray-900 tracking-tight truncate min-w-0">{service.name}</h3>
                      <ServiceToggle
                        checked={isLive}
                        onChange={(checked) => handleToggleServiceActive(service, checked)}
                        loading={togglingServiceId === service.id}
                        disabled={togglingServiceId !== null && togglingServiceId !== service.id}
                      />
                    </div>
                    {service.tagline && (
                      <p className="text-sm text-gray-600 mb-2">{service.tagline}</p>
                    )}
                    {service.description && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                        {service.description}
                      </p>
                    )}
                    {/* Actions — full width grid, both centered equally */}
                    <div className="grid grid-cols-2 border-t border-gray-50 mt-3 -mx-4 overflow-hidden">
                      <button
                        onClick={() => openServiceModal(service)}
                        className="flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 active:bg-gray-100 rounded-bl-2xl transition-colors outline-none focus:outline-none"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={() => openDeleteConfirm(service)}
                        className="flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-red-500 hover:text-red-600 active:bg-red-50 rounded-br-2xl transition-colors outline-none focus:outline-none"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        ) : (
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {services.map((service) => {
                    const isLive = service.active === true;
                    
                    return (
                      <tr key={service.id} className="transition-colors hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {service.images && service.images.length > 0 ? (
                              <img 
                                src={service.images[service.mainImageIndex || 0]} 
                                alt={service.name} 
                                className={`w-12 h-12 rounded-lg object-cover ${!isLive ? 'opacity-60' : ''}`}
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                                <Package className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-gray-900">
                                {service.name}
                              </div>
                              {service.tagline && (
                                <div className="text-xs text-gray-500 hidden sm:block">{service.tagline}</div>
                              )}
                              <div className="text-xs text-gray-600 sm:hidden">
                                {service.currency === 'AED' ? 'AED ' : '$'}{service.price} • {service.duration}min
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="font-semibold text-blue-600">
                            {service.currency === 'AED' ? 'AED ' : '$'}{service.price || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 hidden md:table-cell">
                          {service.duration ? `${service.duration} min` : '-'}
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            <ServiceToggle
                              checked={isLive}
                              onChange={(checked) => handleToggleServiceActive(service, checked)}
                              loading={togglingServiceId === service.id}
                              disabled={togglingServiceId !== null && togglingServiceId !== service.id}
                              size="sm"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openServiceModal(service)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteConfirm(service)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Duplicate Service Picker */}
        {showDuplicatePicker && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Duplicate a Service</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Pick one to copy — you can edit it before saving</p>
                </div>
                <button
                  onClick={() => setShowDuplicatePicker(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400"
                >
                  ×
                </button>
              </div>

              {/* Search */}
              <div className="px-4 pt-3 pb-2">
                <input
                  autoFocus
                  type="text"
                  placeholder="Search services..."
                  value={duplicateSearch}
                  onChange={e => setDuplicateSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="duplicate-search-input"
                />
              </div>

              {/* Service list */}
              <div className="overflow-y-auto max-h-72 px-2 pb-3">
                {services
                  .filter(s => !duplicateSearch || s.name.toLowerCase().includes(duplicateSearch.toLowerCase()))
                  .map(s => (
                    <button
                      key={s.id}
                      data-testid={`duplicate-pick-${s.id}`}
                      onClick={() => duplicateService(s)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                    >
                      {/* Thumbnail */}
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                        {s.images?.[0] ? (
                          <img src={s.images[0]} alt={s.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{s.name}</div>
                        <div className="text-xs text-gray-500">
                          ${s.basePrice || s.price || 0} · {s.durationMinutes || s.duration || 60} min
                        </div>
                      </div>
                      <svg className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ))
                }
                {services.filter(s => !duplicateSearch || s.name.toLowerCase().includes(duplicateSearch.toLowerCase())).length === 0 && (
                  <p className="text-center text-sm text-gray-400 py-6">No services match "{duplicateSearch}"</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Service Modal */}
        <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
          <DialogContent
            className="sm:max-w-xl p-4 sm:p-6 flex flex-col gap-0"
            data-testid="service-builder-modal"
            onPointerDownOutside={(e) => { if (window.__bfTourActive) e.preventDefault(); }}
            onInteractOutside={(e) => { if (window.__bfTourActive) e.preventDefault(); }}
          >
            <DialogTitle className="sr-only">{editingService ? 'Edit service' : 'New service'}</DialogTitle>
            <DialogDescription className="sr-only">Create or edit a bookable service across pricing, availability, location, add-ons and settings.</DialogDescription>
            <CreateServiceWizard
              editingService={editingService}
              onComplete={(savedService) => {
                loadServices();
                setShowServiceModal(false);
                setEditingService(null);
              }}
              onCancel={() => {
                setShowServiceModal(false);
                setEditingService(null);
              }}
              assets={assets}
            />
          </DialogContent>
        </Dialog>
        
        {/* Service Sharing Modal */}
        <ServiceSharingModal
          isOpen={showSharingModal}
          onClose={() => {
            setShowSharingModal(false);
            setSharingService(null);
          }}
          service={sharingService}
          onUpdate={loadServices}
        />
        
        {/* Delete Confirmation Modal */}
        <StandardModal
          isOpen={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, service: null, hasBookings: false, checkingBookings: false })}
          title={deleteConfirm.hasBookings ? "Can't Delete Yet" : "Delete Service?"}
          description={deleteConfirm.hasBookings 
            ? `"${deleteConfirm.service?.name}" has upcoming trips` 
            : `Are you sure you want to delete "${deleteConfirm.service?.name}"?`}
          size="sm"
          footer={
            deleteConfirm.checkingBookings ? (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : deleteConfirm.hasBookings ? (
              <SecondaryButton
                onClick={() => setDeleteConfirm({ open: false, service: null, hasBookings: false, checkingBookings: false })}
                className="w-full"
              >
                Got it
              </SecondaryButton>
            ) : (
              <>
                <SecondaryButton
                  onClick={() => setDeleteConfirm({ open: false, service: null, hasBookings: false, checkingBookings: false })}
                  className="flex-1"
                  disabled={isDeleting}
                >
                  Cancel
                </SecondaryButton>
                <DangerButton
                  onClick={handleDeleteService}
                  className="flex-1"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Service'
                  )}
                </DangerButton>
              </>
            )
          }
        >
          {deleteConfirm.checkingBookings ? (
            <p className="text-gray-500 text-center py-4">Checking for upcoming bookings...</p>
          ) : deleteConfirm.hasBookings ? (
            <InfoCard variant="warning" showIcon={true}>
              <p className="font-medium mb-1">This service has upcoming trips.</p>
              <p>Keep it turned &quot;Off&quot; until all trips are completed, then you can delete it.</p>
            </InfoCard>
          ) : (
            <InfoCard variant="danger" showIcon={true}>
              <p className="font-medium mb-1">This action cannot be undone.</p>
              <p>This service will no longer be bookable. Existing bookings will remain.</p>
            </InfoCard>
          )}
        </StandardModal>
      </div>
    </TabPageLayout>
  );
};

export default ServicesTabImproved;
